import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Header from "../molecules/Header.jsx";
import QuitConfirmation from "../molecules/QuitConfirmation.jsx";
import SendMessage from "../molecules/SendMessage.jsx";
import MessagesContainer from "../molecules/MessagesContainer.jsx";
import {
    CHAT_SESSION_ID,
    GREETING_MSG_PRE,
    KEYS,
} from "../../../constants.js";
import { formatMessageDataV2 } from "../../utils/formatData.js";
import {
    clearRefresher,
    concatMessage,
    incrementPosition,
    setIsEnd,
    setLoadingReply,
    setMessages,
    setMessagesArchived,
    updateExpiredAt,
} from "../../features/messagesSlice.js";
import {
    setFloatingContainerState,
    setFloatingWindowState,
    setHeaderState,
    setSendMessageContainerState,
} from "../../features/componentStateSlice.js";
import TypingText from "../atoms/texts/TypingText.jsx";
import { endChat } from "../../services/chatService.js";
import { Resizable } from "re-resizable";
import useRequestData from "../../hooks/useRequestData.js";

export default function FloatingWindow() {
    const dispatch = useDispatch();
    const floatingWindowState = useSelector(
        (state) => state.componentState.floatingWindowState
    );
    const isTypingContainerState = useSelector(
        (state) => state.componentState.isTypingContainerState
    );

    const isEnd = useSelector((state) => state.messages.isEnd);

    const position = useSelector((state) => state.messages.position);
    const quitConfirmationContainerState = useSelector(
        (state) => state.componentState.quitConfirmationContainerState
    );
    const messagesArchived = useSelector(
        (state) => state.messages.messagesArchived
    );
    const sendMessageContainerState = useSelector(
        (state) => state.componentState.sendMessageContainerState
    );
    const headerState = useSelector(
        (state) => state.componentState.headerState
    );
    const token = useSelector((state) => state.session.token);
    const deviceId = useSelector((state) => state.session.deviceId);

    const messages = useSelector((state) => state.messages.messages);
    const expiredScheduler = useSelector(
        (state) => state.messages.expiredScheduler
    );

    const { initializeChat } = useRequestData();

    const [windowSize, setWindowSize] = useState({
        width: 375,
        height: 550,
    });

    // Set Greeting Message
    useEffect(() => {
        const handlePreloginMessageGreet = async () => {
            const greetingMessage = formatMessageDataV2(
                deviceId,
                "bot",
                GREETING_MSG_PRE,
                position,
                true
            );
            dispatch(concatMessage(greetingMessage));
            dispatch(incrementPosition());
            dispatch(updateExpiredAt());
            dispatch(setLoadingReply(false));
        };

        if (CHAT_SESSION_ID !== null && token) {
            if (messagesArchived.length === 0) {
                handlePreloginMessageGreet();
            }
        }
    }, [CHAT_SESSION_ID, token]);

    useEffect(() => {
        const handleEndChat = async () => {
            if (isEnd) {
                dispatch(clearRefresher());
                clearInterval(expiredScheduler);

                // End previous chat session
                try {
                    await endChat(token);
                } catch (error) {
                    console.error("Error ending chat:", error);
                }

                // Archive messages without restart notification
                localStorage.removeItem(KEYS.ARCHIVEMESSAGE_TOKEN);
                localStorage.removeItem(KEYS.ARCHIVECHAT_ID);
                dispatch(
                    setMessagesArchived([...messagesArchived.concat(messages)])
                );

                // Auto Restart Logic
                dispatch(setIsEnd(false));
                dispatch(setLoadingReply(true));

                try {
                    await initializeChat();
                    const greetingMessage = formatMessageDataV2(
                        deviceId,
                        "bot",
                        GREETING_MSG_PRE,
                        position,
                        true
                    );
                    dispatch(setMessages([greetingMessage]));
                    dispatch(incrementPosition());
                } catch (error) {
                    console.error("Error initializing chat:", error);
                } finally {
                    dispatch(setLoadingReply(false));
                }
            }
        };
        handleEndChat();
    }, [isEnd]);

    return (
        <div>
            {floatingWindowState && (
                <Resizable
                    size={{
                        width: windowSize.width,
                        height: windowSize.height,
                    }}
                    onResizeStop={(e, direction, ref, d) => {
                        setWindowSize({
                            width: windowSize.width + d.width,
                            height: windowSize.height + d.height,
                        });
                    }}
                    minWidth={375}
                    minHeight={250}
                    className="floating-window"
                >
                    <div className="floating-window-container">
                        {headerState && <Header />}
                        <>
                            {quitConfirmationContainerState && (
                                <QuitConfirmation />
                            )}
                            <MessagesContainer />
                            {isTypingContainerState && <TypingText />}
                            {sendMessageContainerState && <SendMessage />}
                        </>
                    </div>
                </Resizable>
            )}
        </div>
    );
}
